import React, { useEffect, useState } from "react";
import { MonitorSmartphone, Trash2, Search, RotateCcw, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DeviceRow = ({ d, onRemove }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm" data-testid={`device-${d.id}`}>
    <span className="text-foreground">{d.label}{d.current && <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-[11px] text-primary" data-testid="device-current">This device</span>}</span>
    <span className="flex items-center gap-3 text-xs text-muted-foreground">
      Last used {(d.last_seen || "").slice(0, 10)}
      {onRemove && <button onClick={() => onRemove(d)} className="rounded-full p-1.5 transition-colors hover:bg-destructive/15 hover:text-destructive" aria-label={`Remove ${d.label}`} data-testid={`device-remove-${d.id}`}><Trash2 size={15} /></button>}
    </span>
  </div>
);

export const MyDevices = () => {
  const { logout } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/auth/devices").then(({ data }) => setD(data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  if (!d?.limited) return null;
  const remove = async (dev) => {
    await api.delete(`/auth/devices/${dev.id}`);
    toast.success(`${dev.label} signed out`);
    if (dev.current) logout(); else load();
  };
  return (
    <section className="mt-12" data-testid="my-devices">
      <h2 className="flex items-center gap-2 font-serif text-2xl"><MonitorSmartphone size={20} className="text-primary" /> Your devices</h2>
      <p className="mt-1 text-sm text-muted-foreground">{d.max > 1 ? `Teacher licences can be used on up to ${d.max} devices.` : "Student accounts can be active on one device at a time. Signing in on another device signs this one out — your progress stays with your account."}</p>
      <p className="mt-2 font-mono text-xs text-primary" data-testid="devices-count">{d.devices.length} of {d.max} devices in use</p>
      <div className="mt-3 space-y-2">{d.devices.map((x) => <DeviceRow key={x.id} d={x} onRemove={remove} />)}</div>
    </section>
  );
};

export const AdminDevices = () => {
  const [email, setEmail] = useState("");
  const [d, setD] = useState(null);
  const lookup = async (e) => {
    e?.preventDefault();
    try { const { data } = await api.post("/admin/devices/lookup", { email }); setD(data); }
    catch (err) { setD(null); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const [sending, setSending] = useState("");
  const testEmail = async (kind) => {
    setSending(kind);
    try { const { data } = await api.post("/admin/devices/test-email", { kind }); toast.success(`Test email sent to ${data.to}`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSending("");
  };
  const reset = async () => {
    await api.post("/admin/devices/reset", { email: d.email });
    toast.success(`Devices reset for ${d.email}`);
    lookup();
  };
  return (
    <section className="mt-14" data-testid="admin-devices">
      <div className="flex items-center gap-2"><MonitorSmartphone size={18} className="text-primary" /><h2 className="font-serif text-2xl">Student devices</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">Students can be active on one device at a time (a new sign-in signs the old device out). Teacher licences can use up to 35 devices. Look up a student to see their devices or reset them if they've changed phone or laptop.</p>
      <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-devices-test-emails">
        <Button variant="outline" size="sm" className="rounded-full" disabled={!!sending} onClick={() => testEmail("new_device")} data-testid="admin-test-new-device-email"><Mail size={14} className="mr-1.5" /> {sending === "new_device" ? "Sending…" : "Send me a test new-device alert"}</Button>
        <Button variant="outline" size="sm" className="rounded-full" disabled={!!sending} onClick={() => testEmail("reset_code")} data-testid="admin-test-reset-code-email"><Mail size={14} className="mr-1.5" /> {sending === "reset_code" ? "Sending…" : "Send me a test reset-code email"}</Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Test emails go to the admin account you're logged in with (subject starts with [TEST]).</p>
      <form onSubmit={lookup} className="mt-4 flex max-w-lg gap-2">
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="student@email.com" data-testid="admin-devices-email" />
        <Button type="submit" className="rounded-full" data-testid="admin-devices-lookup"><Search size={15} className="mr-1.5" /> Look up</Button>
      </form>
      {d && (
        <div className="mt-4 space-y-2" data-testid="admin-devices-result">
          <p className="text-sm text-foreground">{d.name} <span className="text-muted-foreground">· {d.email} · {d.limited ? `${d.devices.length} of ${d.max} devices` : "not limited (teacher/admin)"}</span></p>
          {d.devices.map((x) => <DeviceRow key={x.id} d={x} />)}
          {d.devices.length > 0 && <Button variant="outline" onClick={reset} className="rounded-full" data-testid="admin-devices-reset"><RotateCcw size={15} className="mr-1.5" /> Reset devices</Button>}
        </div>
      )}
    </section>
  );
};
